import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Checkbox,
    Grid,
    List,
    Button,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Badge
} from "@mui/material";
import {useBooleanHistoryState} from "~/apps/core/utils";
import {makeStyles} from "@mui/styles";
import {ArrowDropDown} from "@mui/icons-material";
import {PortTheme} from "~/ui/theme";
import {ChipInput} from "./ChipInput";
import {BottomSheet} from "~/apps/core/ui/BottomSheet";
import {InlineBadge} from "~/apps/core/ui/InlineBadge";


export type MultiSelectChipInputProps<V> = {
    options: { title: React.ReactNode, value: V }[]
    value?: V[]
    onChange?: (value: V[]) => any
    label?: React.ReactNode
    title?: React.ReactNode
    disabled?: boolean
    compareFunction?: (a: V, b: V) => boolean
}

const defaultProps: Partial<MultiSelectChipInputProps<any>> = {
    compareFunction: (a, b) => a == b
}


const useStyles = makeStyles<PortTheme>(theme => ({
    chip: {
        "& .MuiBadge-badge": {
            position: "initial",
            transform: "none",
            marginLeft: 6,
        },
    },
    item: {
        height: 48
    }
}))


export const MultiSelectChipInput = <T extends unknown>(props: MultiSelectChipInputProps<T>) => {
    const {label, title, options, value, onChange, disabled, compareFunction} = props;
    const classes = useStyles(props);
    const [open, setOpen] = useBooleanHistoryState();
    const [nextValue, setNextValue] = useState([]);
    const ref = useRef<HTMLDivElement>();
    const disabledReset = useMemo(() => {
        return !nextValue?.length || disabled;
    }, [nextValue, disabled]);

    useEffect(() => {
        if (open) {
            setNextValue(typeof value == "undefined" ? [] : value);
        }
    }, [value, open]);

    useEffect(() => {
        if (!open) {
            setNextValue([]);
        }
    }, [open]);

    const handleClick = () => {
        ref?.current?.blur();
        setOpen(true).then(() => null);
    }

    const handleClose = () => {
        setOpen(false).then(() => null);
    }

    const handleReadyClick = () => {
        setOpen(false).then(() => {
            onChange && onChange(nextValue.length > 0 ? nextValue : undefined);
        });
    }

    const handleResetClick = () => {
        setOpen(false).then(() => {
            onChange && onChange(undefined);
        });
    }

    const handleOptionClick = (optionValue) => (e: React.SyntheticEvent) => {
        e.stopPropagation();
        const newNextValue = [...nextValue];
        let index = -1;
        for (let i = 0; i < newNextValue.length; ++i) {
            if (compareFunction(newNextValue[i], optionValue)) {
                index = i;
                break
            }
        }

        if (index != -1) {
            newNextValue.splice(index, 1);
        } else {
            newNextValue.push(optionValue);
        }
        setNextValue(newNextValue);
    }

    return (
        <>
            <ChipInput
                ref={ref}
                selected={!!value?.length}
                onClick={handleClick}
                className={classes.chip}
                label={(
                    <>
                        {label || title}
                        {value?.length > 0 && <Badge badgeContent={value?.length} color="additionalPrimary"/>}
                        <ArrowDropDown/>
                    </>
                )}
                color="additionalPrimary"
                disabled={disabled}
            />
            <BottomSheet title={title} open={open} onClose={handleClose}>
                <List>
                    {options.map((option, index) => {
                        const selected = nextValue && nextValue.findIndex(x => compareFunction(x, option.value)) != -1;
                        return (
                            <ListItem key={index} button onClick={handleOptionClick(option.value)}
                                      disabled={disabled} className={classes.item}>
                                <ListItemText primary={option.title} primaryTypographyProps={{color: "primary"}}/>
                                <ListItemSecondaryAction>
                                    <Checkbox
                                        edge="end"
                                        color="additionalPrimary"
                                        onClick={handleOptionClick(option.value)}
                                        checked={selected}
                                        disabled={disabled}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        )
                    })}
                </List>
                <Box p={2} pt={1}>
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <Button
                                color="additionalPrimary"
                                fullWidth
                                endIcon={<InlineBadge badgeContent={nextValue.length} showZero color="onAdditionalPrimary"/>}
                                onClick={handleReadyClick}
                            >
                                Применить
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button color="secondary" fullWidth onClick={handleResetClick} disabled={disabledReset}>
                                Сбросить
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </BottomSheet>
        </>
    )
}
MultiSelectChipInput.defaultProps = defaultProps;